import React from "react";
import PropTypes from "prop-types";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "@helpers/translate";
import { CustomScrollTable } from "../../CustomScrollTable";
import { useOrdersLayout } from "../OrdersLayoutContext";
import { FavoriteStarIcon } from "./FavoriteStarIcon";
import { CommunityMobileList } from "./CommunityMobileList";
import { getClickableProps } from "./rowProps";
import { BADGE_ICONS, COIN_ICONS } from "./communityMockData";
import "./CommunityTables.sass";

export const LeaderboardTable = ({ data = [], loading = false, onRowClick }) => {
  const { __ } = useTranslation();
  const isMobileQuery = useMediaQuery("(max-width: 768px)");
  const { isCompactCards } = useOrdersLayout();
  const isMobile = isMobileQuery || isCompactCards;

  if (isMobile) {
    return (
      <CommunityMobileList
        data={data}
        loading={loading}
        type="leaderboard"
        onItemClick={onRowClick}
      />
    );
  }

  return (
    <CustomScrollTable className="community-table community-table--leaderboard">
      <Table stickyHeader>
        <TableHead>
          <TableRow className="community-table__head-row">
            <TableCell className="community-table__col-lb-rank">
              {__("futures.community.rank")}
            </TableCell>
            <TableCell className="community-table__col-lb-user">
              {__("futures.community.user")}
            </TableCell>
            <TableCell className="community-table__col-lb-trades">
              {__("futures.community.trades")}
            </TableCell>
            <TableCell className="community-table__col-lb-favorite-asset">
              {__("futures.community.favorite_asset")}
            </TableCell>
            <TableCell className="community-table__col-lb-total-wagered">
              {__("futures.community.total_wagered")}
            </TableCell>
            <TableCell className="community-table__col-lb-biggest-win">
              {__("futures.community.biggest_win")}
            </TableCell>
            <TableCell className="community-table__col-lb-total-profit">
              {__("futures.community.total_profit")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => {
            const isProfit = String(row.totalProfit).startsWith("+");
            const isLoss = String(row.totalProfit).startsWith("-");
            const profitClass = isProfit
              ? "community-table__cell--green"
              : isLoss
                ? "community-table__cell--red"
                : "";

            const badgeSrc = BADGE_ICONS[row.badgeIcon];
            const assetIcon =
              COIN_ICONS[row.favoriteAsset?.assetIcon] ||
              COIN_ICONS[row.favoriteAsset?.symbol] ||
              null;

            return (
              <TableRow
                key={row.rank ?? index}
                {...getClickableProps(
                  onRowClick,
                  row,
                  "community-table__body-row",
                  "community-table__body-row--clickable",
                )}
              >
                {/* 1. Rank with optional 3D badge */}
                <TableCell className="community-table__col-lb-rank">
                  <div className="community-table__lb-rank-cell">
                    <div className="community-table__lb-badge-box">
                      {badgeSrc ? (
                        <img
                          src={badgeSrc}
                          alt={`rank ${row.rank}`}
                          className="community-table__lb-badge-icon"
                        />
                      ) : null}
                    </div>
                    <span className="community-table__lb-rank-num">
                      {row.rank}
                    </span>
                  </div>
                </TableCell>

                {/* 2. User */}
                <TableCell className="community-table__col-lb-user">
                  <div className="community-table__lb-user-cell">
                    <button
                      type="button"
                      className="community-table__star-btn"
                      aria-label="favorite"
                    >
                      <FavoriteStarIcon
                        active={row.favoriteIcon === "star-yellow"}
                        size={24}
                      />
                    </button>
                    <span className="community-table__lb-username">
                      {row.user}
                    </span>
                  </div>
                </TableCell>

                {/* 3. Trades */}
                <TableCell className="community-table__col-lb-trades">
                  <span className="community-table__lb-trades-text">
                    {row.trades}
                  </span>
                </TableCell>

                {/* 4. Favorite Asset */}
                <TableCell className="community-table__col-lb-favorite-asset">
                  <div className="community-table__lb-asset-cell">
                    {assetIcon && (
                      <img
                        src={assetIcon}
                        alt={row.favoriteAsset?.symbol || "asset"}
                        className="community-table__lb-coin-icon"
                      />
                    )}
                    <span className="community-table__lb-coin-name">
                      {row.favoriteAsset?.symbol}
                    </span>
                  </div>
                </TableCell>

                {/* 5. Total Wagered */}
                <TableCell className="community-table__col-lb-total-wagered">
                  {row.totalWagered}
                </TableCell>

                {/* 6. Biggest Win */}
                <TableCell className="community-table__col-lb-biggest-win">
                  {row.biggestWin}
                </TableCell>

                {/* 7. Total Profit */}
                <TableCell
                  className={`community-table__col-lb-total-profit ${profitClass}`}
                >
                  {row.totalProfit}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </CustomScrollTable>
  );
};

LeaderboardTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  onRowClick: PropTypes.func,
};

export default LeaderboardTable;
